import { AnimatePresence, motion } from 'framer-motion';
import {
  AlarmClockPlus,
  BadgeCheck,
  Bell,
  Bolt,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  CircleHelp,
  Cloud,
  CloudOff,
  Code2,
  Lightbulb,
  LoaderCircle,
  LogOut,
  RotateCcw,
  Send,
  Trophy,
  Users,
  X,
} from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { BrandLockup } from '../components/brand/DatabinexMark';
import { Button } from '../components/ui/Button';
import { useCountdown } from '../hooks/useCountdown';
import { callFunction, errorMessage } from '../lib/callables';
import { cn } from '../lib/cn';
import { TEAM_QUESTION_COUNT } from '../lib/arena-rules';
import { formatFaClock, formatFaNumber, formatFaText } from '../lib/format';
import { useAuth } from '../providers/AuthProvider';
import type { ArenaEvent, PublicQuestion, TeamPublic, TeamState, TimedScoreBreakdown } from '../types/arena';

const difficultyLabel = { easy: 'آسان', medium: 'متوسط', hard: 'سخت' } as const;
const difficultyClasses = {
  easy: 'bg-emerald-50 text-emerald-700 ring-emerald-100',
  medium: 'bg-amber-50 text-amber-700 ring-amber-100',
  hard: 'bg-red-50 text-red-700 ring-red-100',
} as const;

interface Feedback {
  kind: 'success' | 'error' | 'power';
  title: string;
  detail: string;
}

interface SubmitResult {
  correct?: boolean;
  verdict: string;
  correctnessRatio?: number;
  scoreAwarded?: number;
  scoreBreakdown?: TimedScoreBreakdown;
  optimizationRatio?: number;
  comparisonReason?: string;
  passedRequirements?: string[];
  failedRequirements?: string[];
  retryOffered?: boolean;
  unlocked?: string[];
}

function TopPowerButton({ icon, title, count, disabled, onClick }: { icon: React.ReactNode; title: string; count: number; disabled: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="group relative flex h-16 min-w-0 items-center gap-3 overflow-hidden rounded-2xl border border-white/10 bg-white/[.06] px-3.5 pl-10 text-right transition hover:border-brand/55 hover:bg-brand/15 disabled:cursor-not-allowed disabled:opacity-45"
      dir="rtl"
    >
      <span className="grid size-10 shrink-0 place-items-center rounded-xl bg-white/8 text-brand transition group-hover:bg-brand group-hover:text-white">{icon}</span>
      <span className="min-w-0 flex-1 leading-tight">
        <b className="block truncate text-[15px] font-black text-white">{title}</b>
        <small className="mt-1 block truncate text-[11px] font-bold text-white/52">باقی‌مانده {formatFaNumber(count)}</small>
      </span>
      <span className="absolute left-2.5 top-2 rounded-full bg-white/10 px-2 py-1 text-xs font-black text-white/70">×{formatFaNumber(count)}</span>
    </button>
  );
}

function scoreFeedback(scoreAwarded: number, breakdown?: TimedScoreBreakdown) {
  if (!breakdown) return `${formatFaNumber(scoreAwarded)} امتیاز به تیم اضافه شد.`;
  const speedPercent = Math.round(breakdown.speedRatio * 100);
  const optimizationPercent = Math.round((breakdown.optimizationRatio ?? 0) * 100);
  const optimizationLabel = optimizationPercent > 0 ? ` · بهینه‌سازی ${formatFaNumber(optimizationPercent)}٪` : '';
  const multiplierLabel = breakdown.multiplier < 1 ? ` · ضریب راهنما/فرصت دوباره ${formatFaNumber(Math.round(breakdown.multiplier * 100))}٪` : '';
  return `${formatFaNumber(scoreAwarded)} امتیاز ترکیبی؛ سرعت پاسخ ${formatFaNumber(speedPercent)}٪${optimizationLabel}${multiplierLabel}.`;
}

function queryProblemDetail(result: SubmitResult) {
  if (result.comparisonReason) return result.comparisonReason;
  const missing = result.failedRequirements?.slice(0, 4).join('، ');
  if (missing) return `مشکل Query: ${missing}`;
  const labels: Record<string, string> = {
    wrong_answer: 'Query با rubric این سؤال جور نشد.',
    wrong_query: 'Query معتبر برای این سؤال تشخیص داده نشد.',
    partial_query: 'Query ناقص است.',
    unsafe_query: 'فقط SELECT/CTE مجاز است.',
    empty_answer: 'Query کامل T-SQL را وارد کنید.',
  };
  return labels[result.verdict] ?? 'این تلاش بدون امتیاز ثبت شد.';
}

export function ArenaPage({ event, team, state, question, rank }: { event: ArenaEvent; team: TeamPublic; state: TeamState; question: PublicQuestion | null; rank: number }) {
  const { logout } = useAuth();
  const [answer, setAnswer] = useState(state.draftAnswer || '');
  const [submitting, setSubmitting] = useState(false);
  const [saveStatus, setSaveStatus] = useState<'saved' | 'saving' | 'offline' | 'error'>('saved');
  const [feedback, setFeedback] = useState<Feedback | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const questionId = state.currentQuestionId;
  const previousQuestion = useRef<string | null>(questionId);
  const expiredFor = useRef<string | null>(null);
  const draftTimer = useRef<number | null>(null);
  const remainingMs = useCountdown(state.deadlineMs, event.status === 'running');
  const totalQuestions = state.assignment.length || TEAM_QUESTION_COUNT;
  const routeProgressPercent = Math.min(100, Math.max(0, ((state.currentIndex + 1) / totalQuestions) * 100));
  const locked = event.status !== 'running' || !question || submitting || state.awaitingRetryDecision;
  const lowTime = remainingMs > 0 && remainingMs <= 30_000;

  useEffect(() => {
    if (questionId && previousQuestion.current !== questionId) {
      previousQuestion.current = questionId;
      expiredFor.current = null;
      setAnswer(state.draftAnswer || '');
      setHint(null);
    }
  }, [questionId, state.draftAnswer]);

  useEffect(() => {
    if (!questionId || answer === state.draftAnswer || event.status === 'finished') return;
    setSaveStatus(navigator.onLine ? 'saving' : 'offline');
    if (draftTimer.current !== null) window.clearTimeout(draftTimer.current);
    draftTimer.current = window.setTimeout(() => {
      if (!navigator.onLine) return;
      void callFunction<{ answer: string; questionId: string | null }, { savedAtMs: number; ignored?: boolean }>('updateDraft', { answer, questionId })
        .then(() => setSaveStatus('saved'))
        .catch(() => setSaveStatus('error'));
    }, 900);
    return () => {
      if (draftTimer.current !== null) window.clearTimeout(draftTimer.current);
      draftTimer.current = null;
    };
  }, [answer, event.status, questionId, state.draftAnswer]);

  useEffect(() => {
    const flush = () => {
      if (document.visibilityState === 'hidden' && answer !== state.draftAnswer) {
        void callFunction('updateDraft', { answer, questionId }).catch(() => undefined);
      }
    };
    document.addEventListener('visibilitychange', flush);
    return () => document.removeEventListener('visibilitychange', flush);
  }, [answer, questionId, state.draftAnswer]);

  useEffect(() => {
    if (event.status === 'running' && questionId && remainingMs === 0 && expiredFor.current !== questionId) {
      expiredFor.current = questionId;
      void callFunction<Record<string, never>, { expired: boolean }>('expireQuestion', {})
        .then((result) => result.expired && setFeedback({ kind: 'error', title: 'زمان تمام شد', detail: 'این راند بدون امتیاز ثبت شد.' }))
        .catch(() => {
          expiredFor.current = null;
      });
    }
  }, [event.status, questionId, remainingMs]);

  useEffect(() => {
    if (!feedback) return;
    const timer = window.setTimeout(() => setFeedback(null), 3000);
    return () => window.clearTimeout(timer);
  }, [feedback]);

  const submit = async () => {
    if (draftTimer.current !== null) {
      window.clearTimeout(draftTimer.current);
      draftTimer.current = null;
    }
    setSubmitting(true);
    try {
      const result = await callFunction<
        { answer: string; submissionId: string },
        SubmitResult
      >('submitAnswer', {
        answer,
        submissionId: crypto.randomUUID(),
      });
      if (!result.retryOffered) {
        setAnswer('');
        setSaveStatus('saved');
      }
      if (result.correct) {
        const unlockNames: Record<string, string> = { hint: 'راهنما', extraTime: '۳۰ ثانیه اضافه', retry: 'فرصت دوباره' };
        const unlockedLabel = result.unlocked?.map((item) => unlockNames[item] ?? item).join(' و ');
        setFeedback(
          unlockedLabel
            ? { kind: 'power', title: 'جایزه آزاد شد!', detail: `${unlockedLabel} به کوله‌پشتی تیم اضافه شد.` }
            : { kind: 'success', title: 'درست بود؛ سرعت و کیفیت Query حساب شد!', detail: scoreFeedback(result.scoreAwarded ?? 0, result.scoreBreakdown) },
        );
      } else if (result.retryOffered) {
        setFeedback({ kind: 'power', title: 'یک جان اضافه دارید', detail: `پاسخ درست نبود؛ ${queryProblemDetail(result)} می‌توانید یک بار دیگر Query را اصلاح کنید.` });
      } else if ((result.scoreAwarded ?? 0) > 0) {
        const ratio = Math.round((result.correctnessRatio ?? 0) * 100);
        setFeedback({
          kind: 'power',
          title: 'Query ناقص بود، ولی امتیاز بخشی گرفتید',
          detail: `${formatFaNumber(result.scoreAwarded ?? 0)} امتیاز؛ پوشش rubric حدود ${formatFaNumber(ratio)}٪. ${queryProblemDetail(result)} سؤال بعدی شروع شد.`,
        });
      } else {
        setFeedback({ kind: 'error', title: 'این Query به هدف نخورد', detail: `${queryProblemDetail(result)} این تلاش ثبت شد و سؤال بعدی شروع شد.` });
      }
    } catch (reason) {
      setFeedback({ kind: 'error', title: 'ثبت پاسخ به مشکل خورد', detail: errorMessage(reason) });
    } finally {
      setSubmitting(false);
    }
  };

  const power = async (action: 'hint' | 'extra-time' | 'retry' | 'decline-retry') => {
    try {
      const result = await callFunction<{ action: string }, { hint?: string }>('usePowerUp', { action });
      if (action === 'hint' && result.hint) setHint(result.hint);
      if (action === 'extra-time') setFeedback({ kind: 'power', title: '۳۰ ثانیه اضافه شد', detail: 'زمان جدید ثبت شد؛ نفس تازه کنید و ادامه دهید.' });
      if (action === 'retry') setFeedback({ kind: 'power', title: 'فرصت دوباره فعال شد', detail: 'در تلاش دوم حداکثر ۵۰٪ امتیاز می‌گیرید.' });
      if (action === 'decline-retry') {
        setAnswer('');
        setFeedback(null);
      }
    } catch (reason) {
      setFeedback({ kind: 'error', title: 'جایزه فعال نشد', detail: errorMessage(reason) });
    }
  };

  return (
    <main className="relative min-h-screen overflow-hidden bg-[#172033] text-white lg:h-screen" dir="rtl">
      <div className="arena-grid absolute inset-0 opacity-60" />

      <header className="relative z-10 flex h-12 items-center justify-between px-6">
        <div className="flex items-center gap-3">
          <BrandLockup inverse />
          <div className="hidden h-7 w-px bg-white/10 md:block" />
          <b className="hidden text-sm md:block">DataBinex Arena</b>
          <Trophy className="hidden size-4 text-white/55 md:block" />
        </div>
        <div className="flex items-center gap-4" dir="ltr">
          <span className="grid size-8 place-items-center rounded-full bg-brand text-sm font-black shadow-[0_0_28px_rgba(242,106,33,.35)]">{team.name.slice(0, 1).toUpperCase()}</span>
          <span className="hidden text-[11px] font-bold text-white/55 sm:block">رتبه شما: {formatFaNumber(rank)}</span>
          <button type="button" className="text-white/65 transition hover:text-white"><Bell className="size-5" /></button>
          <button type="button" className="text-white/65 transition hover:text-white"><CircleHelp className="size-5" /></button>
        </div>
      </header>

      <section className="relative z-10 mx-3 grid items-center gap-3 rounded-2xl border border-white/10 bg-white/[.065] px-4 py-3 shadow-[0_20px_70px_rgba(0,0,0,.2)] backdrop-blur-xl lg:grid-cols-[1fr_0.86fr_0.86fr_0.58fr_2.28fr_138px] 2xl:grid-cols-[1fr_0.9fr_0.9fr_0.65fr_2.45fr_150px]">
        <div>
          <span className="block text-xs text-white/50">وضعیت مسابقه</span>
          <b className="mt-1 flex items-center gap-2 text-xl text-emerald-300">
            <span className="size-3 rounded-full bg-emerald-400 shadow-[0_0_16px_#34d399]" /> در حال برگزاری
          </b>
        </div>
        <div className="border-white/10 lg:border-r lg:pr-5">
          <span className="block text-xs text-white/50">زمان باقی‌مانده</span>
          <b className={cn('mt-0.5 block text-2xl tabular-nums tracking-wider', lowTime ? 'text-red-300' : 'text-white')} dir="ltr">{formatFaClock(remainingMs)}</b>
          <span className="mt-1 block text-[10px] text-white/35">ساعت · دقیقه · ثانیه</span>
        </div>
        <div className="border-white/10 lg:border-r lg:pr-5">
          <span className="block text-xs text-white/50">نام تیم</span>
          <b className="mt-1 flex items-center gap-2 truncate text-lg"><Users className="size-5 text-white/50" />{team.name}</b>
        </div>
        <div className="border-white/10 lg:border-r lg:pr-5">
          <span className="block text-xs text-white/50">امتیاز فعلی</span>
          <motion.b key={team.score} initial={{ scale: 1.18, color: '#F26A21' }} animate={{ scale: 1, color: '#FFFFFF' }} className="mt-0.5 block text-2xl font-black tabular-nums">
            {formatFaNumber(team.score)}
          </motion.b>
          <span className="mt-1 block text-[10px] text-white/35">از مسیر {formatFaNumber(TEAM_QUESTION_COUNT)} سؤالی</span>
        </div>
        <div className="min-w-0 border-white/10 lg:border-r lg:pr-5">
          <div className="mb-1.5 flex items-center justify-between gap-2">
            <span className="text-xs font-black text-white/60">راهنماهای تیم</span>
          </div>
          <div className="grid min-w-0 grid-cols-3 gap-2.5">
            <TopPowerButton icon={<Lightbulb className="size-4" />} title="راهنما" count={state.powerUps.hint} disabled={locked || state.powerUps.hint < 1 || state.usedHint} onClick={() => void power('hint')} />
            <TopPowerButton icon={<AlarmClockPlus className="size-4" />} title="۳۰ ثانیه" count={state.powerUps.extraTime} disabled={locked || state.powerUps.extraTime < 1} onClick={() => void power('extra-time')} />
            <TopPowerButton icon={<RotateCcw className="size-4" />} title="دوباره" count={state.powerUps.retry} disabled={!state.awaitingRetryDecision || state.powerUps.retry < 1} onClick={() => void power('retry')} />
          </div>
        </div>
        <div className="border-white/10 lg:border-r lg:pr-5">
          <button type="button" onClick={() => void logout()} className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[.055] text-sm font-black text-white/70 transition hover:bg-white/[.09] hover:text-white">
            خروج از مسابقه <LogOut className="size-5 text-red-400" />
          </button>
        </div>
      </section>

      <div className="relative z-10 grid gap-3 p-3 lg:h-[calc(100vh-152px)] lg:grid-cols-[minmax(0,1fr)_380px] lg:overflow-hidden" dir="ltr">
        <motion.section key={question?.id ?? 'loading'} initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} className="flex min-h-[560px] min-w-0 flex-col overflow-y-auto overflow-x-hidden rounded-[10px] bg-white text-ink shadow-[0_20px_70px_rgba(0,0,0,.28)] lg:min-h-0" dir="rtl">
          {question ? (
            <>
              <div className="flex h-12 items-center justify-between border-b border-slate-200 px-5">
                <button type="button" className="flex items-center gap-2 text-sm font-black text-slate-700">
                  <Bookmark className="size-5 text-slate-500" /> نشان‌گذاری
                </button>
                <div className="flex items-center gap-3" dir="ltr">
                  <button type="button" disabled className="grid size-9 place-items-center rounded-xl border border-slate-200 text-slate-400"><ChevronLeft className="size-5" /></button>
                  <button type="button" disabled className="grid size-9 place-items-center rounded-xl border border-slate-200 text-slate-400"><ChevronRight className="size-5" /></button>
                  <span className="px-3 text-sm font-black text-slate-600" dir="rtl">{formatFaNumber(state.currentIndex + 1)} از {formatFaNumber(totalQuestions)}</span>
                </div>
              </div>

              <div className="flex min-h-0 flex-1 flex-col overflow-y-auto px-5 py-3">
                <div className="grid grid-cols-[1fr_auto] gap-4 rounded-2xl border border-brand/10 bg-brand-pale/45 px-4 py-3">
                  <div>
                    <div className="mb-2 flex items-center gap-2">
                      <span className={cn('rounded-xl px-3 py-1.5 text-xs font-black ring-1', difficultyClasses[question.difficulty])}>{difficultyLabel[question.difficulty]}</span>
                      <span className="rounded-xl bg-brand/10 px-3 py-1.5 text-xs font-black text-brand">{formatFaNumber(question.points)} امتیاز</span>
                    </div>
                    <h1 className="text-[1.7rem] font-black leading-tight text-ink">{question.title}</h1>
                    <div>
                      <QuestionPrompt prompt={question.prompt} />
                    </div>
                  </div>
                  <div className="hidden min-w-36 text-left text-xs text-slate-400 md:block" dir="ltr">
                    <span className="rounded-full border border-slate-200 px-3 py-1">Question {state.currentIndex + 1}</span>
                  </div>
                </div>

                {hint && (
                  <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="mt-3 rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-xs leading-6 text-amber-800">
                    <span className="mb-1 flex items-center gap-2 font-black text-amber-600">
                      <Lightbulb className="size-4" /> راهنما
                    </span>
                    {hint}
                  </motion.div>
                )}

                <div className="mt-3 flex min-h-[200px] flex-1 flex-col overflow-hidden rounded-[10px] border border-slate-200 bg-white">
                  <div className="flex h-10 items-center justify-between border-b border-slate-200 bg-slate-50 px-5">
                    <div className="flex items-center gap-2 text-xs font-black text-slate-600">
                      <span className="grid size-7 place-items-center rounded-lg bg-white text-slate-500 shadow-sm">◎</span>
                      SQL Server (T-SQL)
                    </div>
                    <div className="flex items-center gap-2 text-xs font-black text-brand">
                      <span className="rounded-lg bg-brand/8 px-3 py-1">فرمت خودکار</span>
                      <Code2 className="size-4" />
                    </div>
                  </div>
                  <textarea
                    dir="ltr"
                    value={answer}
                    onChange={(event) => setAnswer(event.target.value)}
                    readOnly={locked}
                    spellCheck={false}
                    placeholder="SELECT ..."
                    className="min-h-0 flex-1 resize-none border-0 bg-white px-6 py-4 text-left text-sm leading-7 text-ink outline-none placeholder:text-slate-300 disabled:opacity-50"
                  />
                  <div className="flex h-10 items-center justify-between border-t border-slate-200 bg-slate-50 px-5 text-xs">
                    <span className="flex items-center gap-2 text-emerald-600">
                      {navigator.onLine ? <Cloud className="size-4" /> : <CloudOff className="size-4 text-red-500" />}
                      {saveStatus === 'saving' ? 'در حال ذخیره…' : saveStatus === 'saved' ? 'بدون خطا' : saveStatus === 'offline' ? 'منتظر اتصال' : 'خطا در ذخیره'}
                    </span>
                    <span className="text-slate-400">فقط Query کامل T-SQL را بنویسید.</span>
                  </div>
                </div>
              </div>

              <div className="sticky bottom-0 flex h-16 items-center justify-between border-t border-slate-200 bg-white px-5" dir="ltr">
                <Button size="lg" disabled={locked || submitting || !answer.trim()} onClick={() => void submit()} className="h-11 min-w-52 bg-emerald-600 shadow-[0_14px_34px_rgba(22,163,74,.24)] hover:bg-emerald-700">
                  {submitting ? <LoaderCircle className="size-4 animate-spin" /> : <Send className="size-4" />} ارسال پاسخ نهایی
                </Button>
                <div className="flex gap-3">
                  <button type="button" disabled className="h-11 rounded-xl border border-slate-200 px-5 text-sm font-black text-brand disabled:opacity-45">سؤال قبلی</button>
                  <button type="button" disabled className="h-11 rounded-xl border border-slate-200 px-5 text-sm font-black text-brand disabled:opacity-45">سؤال بعدی</button>
                </div>
              </div>
            </>
          ) : (
            <div className="grid h-full place-items-center">
              <LoaderCircle className="size-6 animate-spin text-brand" />
            </div>
          )}
        </motion.section>

        <aside className="flex min-h-[560px] flex-col overflow-hidden rounded-[10px] bg-white text-ink shadow-[0_20px_70px_rgba(0,0,0,.28)] lg:min-h-0" dir="rtl">
          <div className="border-b border-slate-200 p-4">
            <h2 className="text-lg font-black">مسیر سؤالات</h2>
            <div className="mt-3 flex gap-2 text-[10px] font-bold text-slate-500">
              <span className="rounded-lg border border-slate-200 px-2.5 py-1.5"><i className="ml-1 inline-block size-2 rounded-full bg-emerald-500" />پاسخ داده شده</span>
              <span className="rounded-lg border border-slate-200 px-2.5 py-1.5"><i className="ml-1 inline-block size-2 rounded-full bg-brand" />سؤال جاری</span>
            </div>
            <div className="mt-3 grid grid-cols-6 gap-2">
              {Array.from({ length: totalQuestions }, (_, index) => (
                <span
                  key={index}
                  className={cn(
                    'grid size-9 place-items-center rounded-xl border text-sm font-black transition',
                    index < state.currentIndex && 'border-emerald-200 bg-emerald-500 text-white shadow-sm',
                    index === state.currentIndex && 'border-brand bg-brand text-white shadow-[0_12px_28px_rgba(242,106,33,.28)]',
                    index > state.currentIndex && 'border-slate-200 bg-white text-slate-500',
                  )}
                >
                  {formatFaNumber(index + 1)}
                </span>
              ))}
            </div>
          </div>

          <div className="border-b border-slate-200 p-4">
            <h3 className="text-base font-black">اطلاعات سؤال جاری</h3>
            <div className="mt-3 divide-y divide-slate-200 rounded-xl border border-slate-200">
              <InfoRow label="سختی" value={question ? difficultyLabel[question.difficulty] : '—'} />
              <InfoRow label="امتیاز" value={question ? `${formatFaNumber(question.points)} امتیاز` : '—'} />
              <InfoRow label="زمان صرف‌شده" value={question ? formatFaClock(Math.max(0, question.timeLimitSec * 1000 - remainingMs)) : '—'} />
            </div>
          </div>

          <div className="border-b border-slate-200 p-4">
            <h3 className="text-base font-black">پیشرفت شما</h3>
            <div className="mt-3 flex items-center gap-4">
              <div className="relative grid size-16 place-items-center rounded-full bg-slate-100">
                <div className="absolute inset-0 rounded-full" style={{ background: `conic-gradient(#F26A21 ${routeProgressPercent}%, #E5E7EB 0)` }} />
                <div className="relative grid size-11 place-items-center rounded-full bg-white">
                  <b className="text-sm text-brand">{formatFaNumber(Math.round(routeProgressPercent))}٪</b>
                </div>
              </div>
              <div>
                <b className="block">{formatFaNumber(Math.min(state.currentIndex + 1, totalQuestions))} از {formatFaNumber(totalQuestions)} سؤال</b>
                <div className="mt-2 h-2 w-44 overflow-hidden rounded-full bg-slate-200">
                  <motion.div className="h-full rounded-full bg-emerald-500" animate={{ width: `${routeProgressPercent}%` }} />
                </div>
              </div>
            </div>
          </div>

          <div className="min-h-0 flex-1 bg-gradient-to-b from-white to-brand-pale/40" />
        </aside>
      </div>

      <AnimatePresence>
        {event.status === 'countdown' && <CountdownOverlay startsAtMs={event.startsAtMs} />}
        {event.status === 'paused' && <PauseOverlay connection={event.pausedReason === 'connection'} />}
        {state.awaitingRetryDecision && (
          <div className="fixed inset-x-0 bottom-5 z-50 mx-auto flex w-fit items-center gap-4 rounded-2xl border border-amber-400/25 bg-[#172033]/95 p-4 shadow-2xl backdrop-blur">
            <CircleHelp className="size-6 text-amber-300" />
            <div>
              <b className="block text-sm">یک شانس دیگر می‌خواهید؟</b>
              <span className="text-[10px] text-white/45">تلاش دوم تا ۵۰٪ امتیاز دارد.</span>
            </div>
            <Button size="sm" onClick={() => void power('retry')}>
              بله، دوباره می‌زنیم
            </Button>
            <Button size="sm" variant="secondary" onClick={() => void power('decline-retry')}>
              نه، سؤال بعدی
            </Button>
          </div>
        )}
        {feedback && (
          <motion.div initial={{ opacity: 0, y: -16, scale: 0.95 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -12 }} className={cn('fixed left-1/2 top-[11rem] z-[60] flex min-w-[420px] max-w-[min(760px,calc(100vw-32px))] -translate-x-1/2 items-center gap-4 rounded-2xl border bg-[#172033]/95 p-4 shadow-2xl backdrop-blur', feedback.kind === 'success' ? 'border-emerald-400/30' : feedback.kind === 'power' ? 'border-amber-400/30' : 'border-red-400/25')}>
            <span className={cn('grid size-11 place-items-center rounded-xl', feedback.kind === 'success' ? 'bg-emerald-400/12 text-emerald-300' : feedback.kind === 'power' ? 'bg-amber-400/12 text-amber-300' : 'bg-red-400/12 text-red-300')}>
              {feedback.kind === 'success' ? <BadgeCheck /> : feedback.kind === 'power' ? <Bolt /> : <X />}
            </span>
            <div className="flex-1">
              <b className="block text-sm">{feedback.title}</b>
              <span className="mt-1 block text-[10px] text-white/50">{feedback.detail}</span>
            </div>
            <span className="h-1 w-16 overflow-hidden rounded-full bg-white/10"><motion.i className="block h-full rounded-full bg-white/45" initial={{ width: '100%' }} animate={{ width: '0%' }} transition={{ duration: 3, ease: 'linear' }} /></span>
          </motion.div>
        )}
      </AnimatePresence>
    </main>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between gap-3 px-4 py-2.5 text-xs">
      <span className="text-slate-400">{label}</span>
      <b className="truncate text-ink" dir="auto">{value}</b>
    </div>
  );
}

function CountdownOverlay({ startsAtMs }: { startsAtMs: number | null }) {
  const remaining = useCountdown(startsAtMs, true);
  const count = Math.max(1, Math.ceil(remaining / 1000));
  const progress = Math.min(100, Math.max(0, ((3000 - Math.min(remaining, 3000)) / 3000) * 100));
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 grid place-items-center bg-[#070B13]/92 px-5 text-white backdrop-blur-xl"
    >
      <div className="arena-grid absolute inset-0 opacity-35" />
      <motion.div
        initial={{ y: 18, scale: 0.96, opacity: 0 }}
        animate={{ y: 0, scale: 1, opacity: 1 }}
        exit={{ y: -10, scale: 0.98, opacity: 0 }}
        transition={{ duration: 0.28, ease: 'easeOut' }}
        className="relative w-full max-w-[430px] overflow-hidden rounded-[28px] border border-white/10 bg-[#0B111D] p-7 text-center shadow-[0_36px_110px_rgba(0,0,0,.55)]"
      >
        <span className="absolute inset-x-10 -top-20 h-40 rounded-full bg-brand/20 blur-3xl" />
        <span className="absolute -bottom-24 left-1/2 size-56 -translate-x-1/2 rounded-full bg-white/8 blur-3xl" />

        <div className="relative">
          <span className="mx-auto inline-flex h-10 items-center rounded-full border border-brand/25 bg-brand/10 px-5 text-xs font-black text-brand">
            آماده‌باش مسابقه
          </span>

          <p className="mt-6 text-sm font-bold text-white/55">مسابقه تا چند لحظه دیگر شروع می‌شود</p>

          <div className="relative mx-auto mt-7 grid size-40 place-items-center rounded-full border border-white/10 bg-black/35 shadow-inner">
            <motion.span
              className="absolute inset-3 rounded-full border border-brand/20"
              animate={{ scale: [1, 1.08, 1], opacity: [0.55, 0.95, 0.55] }}
              transition={{ duration: 1, repeat: Infinity, ease: 'easeInOut' }}
            />
            <AnimatePresence mode="wait">
              <motion.strong
                key={count}
                initial={{ y: 18, scale: 0.72, opacity: 0 }}
                animate={{ y: 0, scale: 1, opacity: 1 }}
                exit={{ y: -18, scale: 1.12, opacity: 0 }}
                transition={{ duration: 0.22, ease: 'easeOut' }}
                className="relative font-sans text-[6.5rem] font-black leading-none text-brand tabular-nums"
              >
                {formatFaNumber(count)}
              </motion.strong>
            </AnimatePresence>
          </div>

          <div className="mt-7 h-2 overflow-hidden rounded-full bg-white/10">
            <motion.div className="h-full rounded-full bg-brand" animate={{ width: `${progress}%` }} transition={{ duration: 0.2, ease: 'linear' }} />
          </div>

          <div className="mt-5 flex items-center justify-center gap-2 text-xs font-bold text-white/45">
            <span className="size-2 rounded-full bg-emerald-400 shadow-[0_0_16px_rgba(52,211,153,.8)]" />
            همه‌چیز آماده است؛ با شروع شمارش، سؤال اول باز می‌شود
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

function QuestionPrompt({ prompt }: { prompt: string }) {
  const segments: Array<{ kind: 'heading' | 'text' | 'list' | 'table'; value: string }> = [];
  for (const rawLine of prompt.split('\n')) {
    const line = rawLine.trimEnd();
    const trimmed = line.trim();
    if (trimmed.startsWith('### ')) {
      segments.push({ kind: 'heading', value: trimmed.replace(/^###\s+/, '') });
      continue;
    }
    const kind = line.includes('|') ? 'table' : trimmed.startsWith('- ') ? 'list' : 'text';
    const previous = segments.at(-1);
    if (previous?.kind === kind) {
      previous.value += `\n${line}`;
    } else {
      segments.push({ kind, value: line });
    }
  }

  return (
    <div className="mt-3 max-h-[210px] space-y-2 overflow-y-auto rounded-2xl border border-brand/10 bg-white/75 p-3 pr-4 shadow-inner scrollbar-thin">
      {segments.map((segment, index) => {
        const value = segment.value.trim();
        if (!value) return null;
        if (segment.kind === 'heading') {
          return (
            <h3 key={index} className="flex items-center gap-2 text-[12px] font-black text-brand">
              <span className="size-1.5 rounded-full bg-brand" />
              {formatFaText(value)}
            </h3>
          );
        }
        if (segment.kind === 'table') {
          return (
            <pre key={index} dir="ltr" className="max-h-24 overflow-auto rounded-xl border border-white/10 bg-[#080D17] p-3 text-left font-mono text-[10px] leading-5 text-slate-200 shadow-inner scrollbar-thin">
              {formatFaText(value)}
            </pre>
          );
        }
        if (segment.kind === 'list') {
          return (
            <ul key={index} className="space-y-1.5">
              {value.split('\n').map((item, itemIndex) => (
                <li key={itemIndex} className="flex gap-2 text-[12px] leading-6 text-slate-600">
                  <span className="mt-2 size-1 shrink-0 rounded-full bg-slate-400" />
                  <span>{formatFaText(item.replace(/^\s*-\s*/, ''))}</span>
                </li>
              ))}
            </ul>
          );
        }
        return (
          <p key={index} className="whitespace-pre-line text-[13px] leading-6 text-slate-600">
            {formatFaText(value)}
          </p>
        );
      })}
    </div>
  );
}

function PauseOverlay({ connection }: { connection: boolean }) {
  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="fixed inset-0 z-40 grid place-items-center bg-ink/74 backdrop-blur-sm">
      <div className="max-w-lg text-center">
        <span className="mx-auto grid size-16 place-items-center rounded-2xl border border-brand/25 bg-brand/10">
          <CloudOff className="size-7 text-brand" />
        </span>
        <h2 className="mt-6 text-2xl font-black">بازی موقتاً مکث شد</h2>
        <p className="mt-3 leading-7 text-slate-300">{connection ? 'یکی از تیم‌ها از خط خارج شده است؛ بازی تا برگشت تیم مکث می‌ماند.' : 'مدیر مسابقه بازی را مکث کرده است. Query و زمان شما محفوظ است.'}</p>
        <span className="mt-6 inline-flex items-center gap-2 text-xs text-white/40">
          <LoaderCircle className="size-4 animate-spin" /> منتظر سوت ادامه
        </span>
      </div>
    </motion.div>
  );
}
